import { z } from 'zod/v4';

/**
 * `GET /api/admin/slcp/analytics` querystring. Both bounds are optional ISO
 * date-times (the dashboards send `startOfDay(...).toISOString()`); `to` is
 * inclusive of the whole day it falls on. Unknown keys are stripped, as the
 * legacy route's Ajv `removeAdditional` did.
 */
export const SlcpAnalyticsQuerySchema = z.object({
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
});

export type SlcpAnalyticsQuery = z.infer<typeof SlcpAnalyticsQuerySchema>;

export const SlcpAnalyticsCompanySchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
});

export const SlcpSharedWithCompanySchema = z.object({
  id: z.string(),
  companyName: z.string().nullable(),
  sharedCount: z.number(),
});

/**
 * The SLCP adoption dashboard in one payload. Rates are raw percentages
 * (`66.66…`), 0 when their denominator is 0. `inacuracyFindings` keeps the
 * legacy spelling — both dashboards read it.
 */
export const SlcpAnalyticsSchema = z.object({
  downloads: z.number(),
  totalCompanies: z.number(),
  syncedCompanies: z.number(),
  companiesUsingSlcpQuestionnaire: z.number(),
  companiesUsingSlcpProfile: z.number(),
  syncRate: z.number(),
  questionnaireUsage: z.number(),
  profileUsage: z.number(),
  totalShares: z.number(),
  capCount: z.number(),
  legalFindings: z.number(),
  inacuracyFindings: z.number(),
  capaCreationQuality: z.object({
    total: z.number(),
    matched: z.number(),
    unmatched: z.number(),
    matchedPercentage: z.number(),
    unmatchedPercentage: z.number(),
  }),
  companies: z.array(SlcpAnalyticsCompanySchema),
  sharedWithCompanies: z.array(SlcpSharedWithCompanySchema),
});

export type SlcpAnalytics = z.infer<typeof SlcpAnalyticsSchema>;
